<template>
  <div class="template-list-container">
    <LibrariesSkeletonLoader
      :is-loading="templatesLoading"
      show-three-dots-menu
    />
    <div v-if="!listLoading && !pageLoading">
      <StickyListHeader
        v-if="paginatedTemplates.length"
        class="template-item-wrapper"
        document-class="template"
        :header-columns="headerColumns"
        :selected-sort="selectedSort"
        :sort-direction="sortDirection"
        top-offset="132px"
        @submit-sort="handleSortClick"
      />
      <NoDataBlock
        v-if="!paginatedTemplates.length"
        document-type="Templates"
      />
      <LibraryList v-else>
        <NoEditorWrapper
          v-for="template in paginatedTemplates"
          :key="template.id"
          :redirect-route="{ query: { preview: template.id, type: 'document' } }"
        >
          <LibraryListItemWrapper
            class="template-item-wrapper"
          >
            <router-link
              class="template-item-link"
              :to="{ name: 'editor-view', params: { id: template.id } }"
              @click.stop.prevent="router.replace({ query: { preview: template.id, type: 'document' } })"
            >
              <TemplateListItem
                :template="template"
                @toggle-update="onToggleUpdate"
              />
            </router-link>
            <ThreeDotsMenu
              :menu-items="menuItems"
              :document="template"
              @click="handleMenuItemClick"
            />
          </LibraryListItemWrapper>
        </NoEditorWrapper>
      </LibraryList>
      <StickyPagination
        v-if="totalPages > 1"
        :model-value="page"
        :length="totalPages"
        @update:model-value="(value) => sendFilterRequest({ pageNumber: value, router: $router })"
      />
    </div>
    <DraftTemplateModal
      :show="showToggleModal"
      :is-template-hidden="Boolean(draftTemplateToHide?.isHidden)"
      @close="showToggleModal = false"
      @update="onUpdate"
    />
    <DeleteModal
      :show="deleteModalVisible"
      document-type="job_template"
      :name="templateToDelete?.title || ''"
      @close="onDeleteClose"
      @delete="onDeleteAction"
    />
    <DuplicateModal
      :show="duplicateModalVisible"
      document-type="job_template"
      :doc-title="templateToDuplicate?.title || ''"
      @close="onDuplicateClose"
      @duplicate="onDuplicateAction"
    />
  </div>
</template>

<script setup>
import LibraryList from '@/components/shared/LibraryList.vue';
import LibraryListItemWrapper from '@/components/shared/LibraryListItemWrapper.vue';
import LibrariesSkeletonLoader from '@/components/shared/SkeletonLoaders/LibrariesSkeletonLoader.vue';
import StickyPagination from '@/components/shared/StickyPagination.vue';
import NoDataBlock from '@/components/library/NoDataBlock.vue';
import DraftTemplateModal from '@/components/editor/DraftTemplateModal.vue';
import DuplicateModal from '@/components/shared/DuplicateModal.vue';
import DeleteModal from '@/components/shared/DeleteModal.vue';
import ThreeDotsMenu from '@/components/shared/ThreeDotsMenu.vue';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { storeToRefs } from 'pinia';
import { useTemplates } from '@/stores/settings/templates';
import { useAppLoading } from '@/stores/app/loading';
import { useLibrary } from '@/stores/library/library';
import { useFilters } from '@/stores/filters/filters';
import {
  computed, onBeforeUnmount, onMounted, ref,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useModals } from '@/stores/modals/modals';
import StickyListHeader from '@/components/library/StickyListHeader.vue';
import NoEditorWrapper from '@/components/editor/NoEditorWrapper.vue';
import TemplateListItem from '@/components/settings/templates/TemplateListItem.vue';
import { useTracking } from '@/stores/tracking/tracking';
import { stickyHeadersTemplates } from '@/helpers/constants/stickyHeaders';

// Stores and Helpers
const {
  templates, sort, sortDirection, selectedSort, paginatedTemplates, totalPages, page,
} = storeToRefs(useTemplates());
const { pageLoading, listLoading, sidebarLoading } = storeToRefs(useAppLoading());

const { setModal } = useModals();

const {
  loadTemplates,
  makeDefault,
  deleteTemplate,
  toggleIsHidden,
  submitSort,
  templatesFilterUpdate,
  resetState,
} = useTemplates();

const { duplicateDocumentAndRedirect } = useLibrary();

const {
  sendFilterRequest,
  setFilterConfig,
  clearPersistentFilters,
} = useFilters();

const router = useRouter();
const route = useRoute();

// State
const showToggleModal = ref(false);
const draftTemplateToHide = ref(null);

const duplicateModalVisible = ref(false);
const deleteModalVisible = ref(false);

const templateToDelete = ref(null);
const templateToDuplicate = ref(null);

const menuItems = ref([
  { text: 'Make Default', icon: '$keep-outline', value: 'default' },
  { text: 'Duplicate Template', icon: '$content-copy-outline', value: 'duplicate' },
  { text: 'Delete Template', icon: '$delete-outline', value: 'delete' },
]);

const props = defineProps({
  // Comes from url paramter set in editor page
  from: {
    type: String,
    default: '',
  },
});

// Computed
const templatesLoading = computed(() => (
  pageLoading.value
    || listLoading.value
    || sidebarLoading.value
));

const headerColumns = computed(() => [
  'score',
  'name',
  'createdBy',
  'createdDate',
  'lastEdited',
  'status',
].map((header) => stickyHeadersTemplates[header])
  .filter(Boolean));

// Lifecycle Hooks
onMounted(async () => {
  if (props.from) {
    setModal({ field: 'settings', value: true });
  }

  setFilterConfig({
    module: 'templates',
    showMyJobs: true,
    datepicker: false,
    subMenuType: 'chip',
  });

  subscribeToFilterUpdates(templatesFilterUpdate);

  const routeQuery = route.query || {};

  await loadTemplates({
    routeQuery,
    router,
    page: parseInt(routeQuery.page, 10) || 1,
    selectedMyJobs: routeQuery.owned === 'true',
  });
});

onBeforeUnmount(() => {
  resetState();
  clearPersistentFilters();
  setFilterConfig({ subMenuType: null });

  unsubscribeToFilterUpdates();
});

// Methods
const handleSortClick = (selectedId) => {
  if (selectedId === selectedSort.value) {
    const optionId = sort.value.includes('-') ? sort.value.replace('-', '') : `-${sort.value}`;

    submitSort({ optionId, router });
  } else {
    const optionId = ['title', 'score', 'created_by'].includes(selectedId) ? selectedId : `-${selectedId}`;

    submitSort({ optionId, router });
  }
};

const handleMenuItemClick = (option, document) => {
  const templateId = document.id;

  if (option === 'default') makeDefault(document.id, document.title);
  else if (option === 'duplicate') {
    const template = templates.value.find((t) => t.id === templateId);

    duplicateModalVisible.value = true;
    templateToDuplicate.value = template;
  } else if (option === 'delete') {
    const template = templates.value.find((t) => t.id === templateId);

    deleteModalVisible.value = true;
    templateToDelete.value = template;
  }
};

const onToggleUpdate = ({ id, isHidden }) => {
  draftTemplateToHide.value = { id, isHidden };
  showToggleModal.value = true;
};

const onUpdate = () => {
  const { id, isHidden } = draftTemplateToHide.value;

  showToggleModal.value = false;
  useTracking().templateHideShowTemplate({
    isHidden: !isHidden,
    origin: 'Library',
  });
  toggleIsHidden(id, isHidden);
};

const onDeleteClose = () => {
  deleteModalVisible.value = false;
  templateToDelete.value = null;
};

const onDeleteAction = async () => {
  deleteModalVisible.value = false;
  await deleteTemplate(templateToDelete.value);
  templateToDelete.value = null;
};

const onDuplicateClose = () => {
  duplicateModalVisible.value = false;
  templateToDuplicate.value = null;
};

const onDuplicateAction = async ({ title }) => {
  duplicateModalVisible.value = false;

  await duplicateDocumentAndRedirect({
    jobId: templateToDuplicate.value.id,
    isPrivate: true,
    title,
    from: 'library-menu',
    router,
  });

  templateToDuplicate.value = null;
};
</script>

<script>
export default { name: 'TemplateList' };
</script>

<style lang="less" scoped>
.template-item-wrapper {
  display: grid;
  grid-template-columns: 1fr 50px;
}

.template-item-link {
  color: @text-light;

  &:visited {
    color: @text-light;
  }
}

.document-list-item, :deep(.sticky-header-items.template) {
  grid-template-columns: 80px minmax(240px, 1fr) 134px 104px 92px 134px;
}

:deep(.sortable-column.score) {
  padding-left: 24px;
}

:deep(.v-skeleton-loader__list-item-two-line) {
  transform: translateY(-10px);
}
</style>
